import React, { useCallback } from "react";
import { connect } from "react-redux";

import { faFilter } from "@fortawesome/pro-light-svg-icons/faFilter";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cn from "classnames";
import { useIntl } from "react-intl";

import Feature from "@mapmycustomers/shared/enum/Feature";
import { EntityType, EntityTypesSupportedByMapsPage } from "@mapmycustomers/shared/types/entity";
import { Button, EntityTypeIcon } from "@mapmycustomers/ui";

import useView from "@app/component/view/utils/useView";
import configService from "@app/config/ConfigService";
import authService from "@app/store/auth/AuthService";
import { openOnMapPage } from "@app/store/map/actions";
import convertFilterModelToMapFilterModelForEntity from "@app/store/savedFilters/convertFilterModelToMapFilterModel";
import { getLegacyAppUrl } from "@app/util/appUrl";
import useFeatures from "@app/util/hook/useFeatures";

import styles from "./SubHeader.module.scss";

interface OwnProps {
  actions?: React.ReactNode;
  className?: string;
  entityType: EntityType;
  iconContainerClassName?: string;
  mapViewLegacyPath: string;
  title: string;
  totalFilteredRecords: number;
  totalRecords: number;
}

interface Props extends OwnProps {
  onOpenOnMapPage: typeof openOnMapPage;
}

const SubHeader: React.FC<Props> = ({
  actions,
  className,
  entityType,
  iconContainerClassName,
  mapViewLegacyPath,
  onOpenOnMapPage,
  title,
  totalFilteredRecords,
  totalRecords,
}) => {
  const intl = useIntl();
  const {
    viewState: { filter, selectedSavedFilterId },
  } = useView();
  const baseOldAppUrl = configService.getBaseOldAppUrl();
  const token = authService.getToken();
  const { hasFeature } = useFeatures();

  const handleViewMapClick = useCallback(() => {
    const mapFilterForEntity = convertFilterModelToMapFilterModelForEntity(
      entityType as EntityTypesSupportedByMapsPage,
      filter
    );
    const updatedFilter = {
      [EntityType.COMPANY]: {},
      [EntityType.DEAL]: {},
      [EntityType.PERSON]: {},
      ...mapFilterForEntity,
    };
    onOpenOnMapPage?.({
      mapViewState: {
        filter: updatedFilter,
        selectedSavedFilterId,
        visibleEntities: [entityType as EntityTypesSupportedByMapsPage],
      },
    });
  }, [entityType, filter, onOpenOnMapPage, selectedSavedFilterId]);

  if (!token) {
    return null;
  }

  return (
    <section className={cn(styles.container, className)}>
      <div className={styles.mainSection}>
        <div className={cn(styles.iconContainer, iconContainerClassName)}>
          <EntityTypeIcon className={styles.icon} entityType={entityType} size="xs" />
        </div>
        <div className={styles.heading}>
          <div className={styles.headingText}>
            <span className={styles.title} data-element-id="page-title">
              {title}
            </span>
            {hasFeature(Feature.MAP) ? (
              <Button onClick={handleViewMapClick} type="link">
                {intl.formatMessage({
                  id: "listView.subHeader.viewMap",
                  defaultMessage: "View Map",
                  description: "List grid subheader view map",
                })}
              </Button>
            ) : (
              <Button href={getLegacyAppUrl(baseOldAppUrl, token, mapViewLegacyPath)} type="link">
                {intl.formatMessage({
                  id: "listView.subHeader.viewMap",
                  defaultMessage: "View Map",
                  description: "List grid subheader view map",
                })}
              </Button>
            )}
          </div>
          <span className={styles.recordCount}>
            {totalFilteredRecords === totalRecords || !totalRecords
              ? intl.formatMessage(
                  {
                    id: "listView.subHeader.title.count",
                    defaultMessage: "{totalRecords, select, 0 {} other {{totalRecords}}} shown",
                    description: "Number of records on subheader in a list view",
                  },
                  { totalRecords }
                )
              : intl.formatMessage(
                  {
                    id: "listView.subHeader.title.countWithFilter",
                    defaultMessage: "{totalFilteredRecords} / {totalRecords} shown",
                    description: "Number of filtered of total records on subheader in a list view",
                  },
                  {
                    filterIcon: <FontAwesomeIcon icon={faFilter} size="xs" />,
                    totalFilteredRecords,
                    totalRecords,
                  }
                )}
          </span>
        </div>
      </div>
      {actions && <div className={styles.actions}>{actions}</div>}
    </section>
  );
};

const mapDispatchToProps = {
  onOpenOnMapPage: openOnMapPage,
};

export default connect(null, mapDispatchToProps)(SubHeader);
